// @noflow

import * as React from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Select from "react-select";
import { translate } from "../IntlProviderWrapper";
import { Dropdown } from "react-bootstrap";
import { getBackend } from "../../lib/backend/Backend2";
import * as BusinessLogic from "../../lib/BusinessLogic";
import { LINE_GROUP_TAGS, INSIGHT_TYPES, PLANT_TAGS_ACCESS } from "../../lib/ManufacturingTags.generated";
import {
    weekNumber,
    getDateOfISOWeek,
    TIME_RANGES,
    fromShiftTag,
    classNames,
    deepClone,
    shiftNumber,
    dayString,
} from "../../lib/Util";

import { NavigationBarWithDropdown, type HandleViewChangeEvent } from "../NavigationBar";

import ShiftAssignments from "./ShiftAssignments";
import { initShiftFilterProps } from "./ShiftAssignments";
import type { ShiftsFilterProps } from "./ShiftAssignments";
import WorkforceKPIs from "./WorkforceKPIs";
import InsightsView from "./InsightsView";
import Authorization from "../Authorization";
import * as Auth from "../../lib/Auth";
import { PEOPLE_VIEW_TYPES, redirectSelectedView } from "../ViewsCommon";
import TextInputModal from "./TextInputModal";
import ConfirmationModal from "../ConfirmationModal";

import type { IPeopleScheduleTemplate } from "../../lib/backend/manufacturing2.generated.types";

import "./PeopleView.scss";

const VIEW_TYPES = PEOPLE_VIEW_TYPES;

type PeopleViewParams = {
    id?: string;
    type: string;
    title?: string;
    title_code?: string;
    dashboard?: string;
};

type Props = {
    history: History;
    match: match<{ selected_view?: string; view_params?: string }>;
    location: Location;
    plant_uuid: string;
    line_group_uuid: string;
};

type State = {
    views: PeopleViewParams[];
    selected_view: string;
    // filters for individual views
    shifts_filter: ShiftsFilterProps;
    template_mode: boolean;
    templates: IPeopleScheduleTemplate[];
    new_template: IPeopleScheduleTemplate | null;
    selected_template: IPeopleScheduleTemplate | null;
    show_template_name_modal: boolean;
    show_delete_template_modal: boolean;
    isSavingShiftBoard: boolean;
};

const templatesMatch = (a: IPeopleScheduleTemplate | null, b: IPeopleScheduleTemplate | null) => {
    return JSON.stringify(a) === JSON.stringify(b);
};

class PeopleView extends React.Component<Props, State> {
    ShiftAssignmentsRef: React.RefObject<ShiftAssignments>;
    constructor(props: Props) {
        super(props);
        const state: State = {
            views: [],
            selected_view: VIEW_TYPES.shifts.id,
            shifts_filter: initShiftFilterProps(),
            template_mode: false,
            templates: [],
            new_template: null,
            selected_template: null,
            show_template_name_modal: false,
            show_delete_template_modal: false,
            isSavingShiftBoard: false,
        };
        this.state = state;
        this.ShiftAssignmentsRef = React.createRef();
    }

    async componentDidMount() {
        await BusinessLogic.waitForManufacturingAsync();
        this.loadViews();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.plant_uuid !== this.props.plant_uuid ||
            prev_props.line_group_uuid !== this.props.line_group_uuid ||
            (this.props.match.params.view_params &&
                prev_props.match.params.view_params !== this.props.match.params.view_params) ||
            (this.props.match.params.selected_view &&
                this.props.match.params.selected_view !== this.state.selected_view)
        ) {
            await BusinessLogic.waitForManufacturingAsync();
            this.loadViews();
        }

        if (
            (prev_props.plant_uuid !== this.props.plant_uuid ||
                prev_props.line_group_uuid !== this.props.line_group_uuid) &&
            this.state.selected_view === VIEW_TYPES.shifts.id &&
            this.state.template_mode
        ) {
            await this.loadTemplates();
        }
    }

    loadViews() {
        // get list of lines from the plant / line_group
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        if (line_group === null) {
            return;
        }

        // create views
        const views: PeopleViewParams[] = [];
        if (line_group.tags && line_group.tags[LINE_GROUP_TAGS.shift_board] === "true") {
            views.push({
                id: VIEW_TYPES.shifts.id,
                type: VIEW_TYPES.shifts.id,
                title: VIEW_TYPES.shifts.title,
                title_code: VIEW_TYPES.shifts.title_code,
            });
            views.push({
                id: VIEW_TYPES.people_insights.id,
                type: VIEW_TYPES.people_insights.id,
                title: VIEW_TYPES.people_insights.title,
                title_code: VIEW_TYPES.people_insights.title_code,
            });
            views.push({
                id: VIEW_TYPES.workforce_kpis.id,
                type: VIEW_TYPES.workforce_kpis.id,
                title: VIEW_TYPES.workforce_kpis.title,
                title_code: VIEW_TYPES.workforce_kpis.title_code,
            });
        }

        // assing appropriate view
        const url_view = this.props.match.params.selected_view;
        let selected_view = "";
        if (url_view) {
            // we have view selected by the URL, check if even possible
            if (views.find(view => view.id === url_view)) {
                // all is good, mark it selected
                selected_view = url_view;
            } else {
                // url_view no longer available, select the top one
                selected_view = views.length > 0 && views[0].id ? views[0].id : "";
            }
        } else {
            // no view indicated in url, select the top one
            selected_view = views.length > 0 && views[0].id ? views[0].id : "";
        }
        this.setState({ views, selected_view });
        if (views.length > 0 && this.props.match.params.view_params) {
            const view_params = this.props.match.params.view_params;
            this.props.history.push(`/manufacturing/people/${selected_view}/${view_params}`);
        } else {
            this.props.history.push(`/manufacturing/people/${selected_view}`);
        }
    }

    handleViewChange = (event: HandleViewChangeEvent) => {
        // read new value
        const selected_view = (event.currentTarget as HTMLButtonElement)?.value ?? "";
        // find new id
        this.setState({ selected_view });
        redirectSelectedView(selected_view, this.props.history);
    };

    /////////////////////////////////////////
    // Shifts
    getTemplates = async (): Promise<IPeopleScheduleTemplate[]> => {
        try {
            const { templates } = await getBackend().manufacturing.getPeopleScheduleTemplates({
                line_group_uuid: this.props.line_group_uuid,
            });
            templates.sort((a, b) => a.name.localeCompare(b.name));
            return templates;
        } catch (error: unknown) {
            console.error("Failed to load templates", error);
        }

        return [];
    };

    loadTemplates = async (): Promise<void> => {
        const templates = await this.getTemplates();
        const selected_template = templates.length > 0 ? templates[0] : null;
        const new_template: IPeopleScheduleTemplate = selected_template
            ? deepClone(selected_template)
            : this.getEmptyTemplate();
        this.setState({
            new_template,
            selected_template,
            templates,
        });
    };

    getEmptyTemplate = (): IPeopleScheduleTemplate => {
        return {
            name: "",
            line_group_uuid: this.props.line_group_uuid,
            lines: [],
        };
    };

    isTemplateNameValid = (name: string): boolean => {
        if (this.state.templates.find(t => t.name === name)) {
            return false;
        }

        return true;
    };

    handleShiftWeekClick(val: number) {
        // handle year flip
        const dt = getDateOfISOWeek(this.state.shifts_filter.week, this.state.shifts_filter.year);
        dt.setDate(dt.getDate() + val * 7);
        const week = weekNumber(dt);
        let year = dt.getFullYear();
        const month = dt.getMonth();
        // fixing problem, when week starts before first day of the year
        if (month === 11 && week === 1) {
            year = year + 1;
        }
        // update state
        const shifts_filter: ShiftsFilterProps = {
            year,
            week,
        };
        this.setState({ shifts_filter });
    }

    handleShiftCopyPreviousWeek = () => {
        this.ShiftAssignmentsRef.current?.handleCopyPreviousWeek();
    };

    handleShiftPrintWeekend = () => {
        this.ShiftAssignmentsRef.current?.exportToHtmlWeekend();
    };

    handleShiftPrintWeek = (params: {
        include_prev_sunday?: boolean;
        include_next_sunday?: boolean;
        columns_as_shifts?: boolean;
    }) => {
        this.ShiftAssignmentsRef.current?.handleShiftPrintWeek(params);
    };

    handleShiftPrintDay = (shift_tag: string) => {
        this.ShiftAssignmentsRef.current?.handleShiftPrintDay(shift_tag);
    };

    handleShiftSaveChanges = () => {
        this.ShiftAssignmentsRef.current?.handleSaveChanges();
    };

    handleTemplateModeToggle = async () => {
        if (!this.state.template_mode && this.state.templates.length === 0) {
            await this.loadTemplates();
        }

        this.setState(prev_state => ({
            ...prev_state,
            template_mode: !prev_state.template_mode,
        }));
    };

    handleTemplateSelectionChange = (value: IPeopleScheduleTemplate | null) => {
        const selected_template = value;
        const new_template: IPeopleScheduleTemplate = selected_template
            ? deepClone(selected_template)
            : this.getEmptyTemplate();
        this.setState(prev_state => ({
            ...prev_state,
            selected_template,
            new_template,
        }));
    };

    handleTemplateChange = (template: IPeopleScheduleTemplate | null) => {
        this.setState(prev_state => ({
            ...prev_state,
            new_template: template,
        }));
    };

    handleShiftSaveStateChange = (inProgress: boolean) => {
        this.setState({ isSavingShiftBoard: inProgress });
    };

    handleTemplateSave = () => {
        this.setState({
            show_template_name_modal: true,
        });
    };

    handleTemplateNameModalAccept = async (name: string) => {
        this.setState({
            show_template_name_modal: false,
        });
        if (this.state.new_template === null) {
            return;
        }

        const new_template: IPeopleScheduleTemplate = {
            ...this.state.new_template,
            name,
        };
        this.setState(
            {
                new_template,
                selected_template: new_template,
            },
            async () => {
                try {
                    await getBackend().manufacturing.updatePeopleScheduleTemplate({
                        template: new_template,
                    });
                    const templates = await this.getTemplates();
                    this.setState({
                        templates,
                    });
                } catch (error: unknown) {
                    console.error("Failed to save template", error);
                }
            }
        );
    };

    handleTemplateNameModalCancel = () => {
        this.setState({
            show_template_name_modal: false,
        });
    };

    handleTemplateDelete = () => {
        this.setState({
            show_delete_template_modal: true,
        });
    };

    handleTemplateDeleteAccept = async () => {
        this.setState({
            show_delete_template_modal: false,
        });

        const template = this.state.selected_template;
        if (template === null) {
            return;
        }

        try {
            await getBackend().manufacturing.deletePeopleScheduleTemplate({
                line_group_uuid: template.line_group_uuid,
                name: template.name,
            });
            await this.loadTemplates();
        } catch (error: unknown) {
            console.error("Failed to delete template", error);
        }
    };

    handleTemplateDeleteCancel = () => {
        this.setState({
            show_delete_template_modal: false,
        });
    };

    handleApplyTemplate = async () => {
        if (this.state.selected_template === null) {
            return;
        }

        if (this.ShiftAssignmentsRef.current?.handleApplyShiftTemplate()) {
            await this.ShiftAssignmentsRef.current.handleSaveChangesSync();
            this.handleTemplateModeToggle();
        }
    };

    firstDayOfWeek = (week: number, year: number) => {
        const { start_shift_tag } = BusinessLogic.startEndShiftTags(year, week, this.props.plant_uuid);
        return fromShiftTag(start_shift_tag);
    };

    isWeekShifted = () => {
        const { start_shift_tag, end_shift_tag } = BusinessLogic.startEndShiftTags(
            this.state.shifts_filter.year,
            this.state.shifts_filter.week,
            this.props.plant_uuid
        );
        const start_week = weekNumber(fromShiftTag(start_shift_tag));
        const end_week = weekNumber(fromShiftTag(end_shift_tag));
        return start_week !== end_week;
    };

    renderShiftFilter() {
        const { week, year } = this.state.shifts_filter;
        const first_day = this.firstDayOfWeek(week, year);
        const base_time = first_day.getTime();
        const days_to_print = this.isWeekShifted() ? 8 : 7; // 7 days for normal week, 8 days for shifted week
        const shift_tags: string[] = [];

        // Allows the week to start from any given day since we calculate the days of the week from the first day of the week
        const days_of_week = Array.from({ length: days_to_print }, (_, index) => {
            const date = new Date(base_time + index * TIME_RANGES.DAY);
            const { shift_tag, day_number } = shiftNumber(date);
            shift_tags.push(shift_tag);
            const day_name = dayString(day_number);
            return `${day_name} - ${date.getDate()}. ${date.getMonth() + 1}.`;
        });
        const plant_tags = BusinessLogic.getPlantTags(this.props.plant_uuid);

        // compose filters
        return (
            <React.Fragment>
                <li className="list-inline-item">
                    {PLANT_TAGS_ACCESS.shift_board_templates(plant_tags) && (
                        <div className="form-group">
                            <div className="btn-group btn-group-toggle view-toggle">
                                <label
                                    className={classNames("btn btn-primary mr-0", {
                                        active: !this.state.template_mode,
                                    })}
                                >
                                    <input
                                        type="radio"
                                        name="view"
                                        value="schedule"
                                        checked={!this.state.template_mode}
                                        onChange={this.handleTemplateModeToggle}
                                    />
                                    <i className="fas fa-calendar-week" />
                                    <span className="ml-2">{translate("common.schedule", "Schedule")}</span>
                                </label>
                                <label
                                    className={classNames("btn btn-primary mr-0", {
                                        active: this.state.template_mode,
                                    })}
                                >
                                    <input
                                        type="radio"
                                        name="view"
                                        value="templates"
                                        checked={this.state.template_mode}
                                        onChange={this.handleTemplateModeToggle}
                                    />
                                    <i className="far fa-copy" />
                                    <span className="ml-2">{translate("Shifts.templates", "Templates")}</span>
                                </label>
                            </div>
                        </div>
                    )}
                </li>
                <li className="list-inline-item">
                    <label>
                        <FormattedMessage id="common.week" defaultMessage="Week" />
                    </label>
                    <div className="form-group">
                        <span className="week-picker">
                            <button className="btn btn-light btn-short" onClick={() => this.handleShiftWeekClick(-1)}>
                                &lt;
                            </button>
                            <span>
                                {this.state.shifts_filter.week} / {this.state.shifts_filter.year}
                            </span>
                            <button className="btn btn-light btn-short" onClick={() => this.handleShiftWeekClick(+1)}>
                                &gt;
                            </button>
                        </span>
                    </div>
                    {!this.state.template_mode && (
                        <div className="form-group">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={this.handleShiftCopyPreviousWeek}
                            >
                                <FormattedMessage
                                    id="ShiftDetails.copy_prev_week"
                                    defaultMessage="Copy previous week"
                                />
                            </button>
                        </div>
                    )}
                    {!this.state.template_mode && (
                        <div className="form-group">
                            <div className="btn-group">
                                <Dropdown>
                                    <Dropdown.Toggle as="button" type="button" className="btn btn-outline-primary">
                                        <FormattedMessage id="common.print" defaultMessage="Print" />
                                        <i className="fas fa-chevron-down" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: false,
                                                    include_next_sunday: true,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_mon_sun"
                                                defaultMessage="Whole week (Mon-Sun)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: true,
                                                    include_next_sunday: false,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_sun_sat"
                                                defaultMessage="Whole week (Sun-Sat)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: true,
                                                    include_next_sunday: true,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_sun_sun"
                                                defaultMessage="Whole week (Sun-Sun)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: false,
                                                    include_next_sunday: true,
                                                    columns_as_shifts: true,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_by_shift_mon_sun"
                                                defaultMessage="Whole week by shift (Mon-Sun)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: true,
                                                    include_next_sunday: false,
                                                    columns_as_shifts: true,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_by_shift_sun_sat"
                                                defaultMessage="Whole week by shift (Sun-Sat)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={() =>
                                                this.handleShiftPrintWeek({
                                                    include_prev_sunday: true,
                                                    include_next_sunday: true,
                                                    columns_as_shifts: true,
                                                })
                                            }
                                        >
                                            <FormattedMessage
                                                id="ShiftDetails.print_whole_week_by_shift_sun_sun"
                                                defaultMessage="Whole week by shift (Sun-Sun)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item as="button" onClick={this.handleShiftPrintWeekend}>
                                            <FormattedMessage
                                                id="ShiftDetails.print_weekend"
                                                defaultMessage="Weekend (Fri,Sat,Sun)"
                                            />
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {days_of_week.map((day, i) => (
                                            <Dropdown.Item
                                                as="button"
                                                key={shift_tags[i]}
                                                onClick={() => this.handleShiftPrintDay(shift_tags[i])}
                                            >
                                                {day}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    )}
                    {!this.state.template_mode && (
                        <div className="form-group mr-0">
                            <Authorization.button
                                permission={Auth.PERMISSION_NAMES.ShiftBoardEdit}
                                type="button"
                                className="btn btn-primary"
                                disabled={this.state.isSavingShiftBoard}
                                onClick={this.handleShiftSaveChanges}
                            >
                                {this.state.isSavingShiftBoard && <i className="fas fa-spinner fa-spin mr-1" />}
                                <FormattedMessage id="common.save" defaultMessage="Save" />
                            </Authorization.button>
                        </div>
                    )}
                    {this.state.template_mode && (
                        <div className="form-group d-inline-flex">
                            <Select
                                className="react-select react-select--sm template-select"
                                classNamePrefix="react-select"
                                placeholder={`${translate("Shifts.select_template", "Select template")}...`}
                                value={this.state.selected_template}
                                options={this.state.templates}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option.name}
                                components={{
                                    IndicatorSeparator: null,
                                }}
                                onChange={value => this.handleTemplateSelectionChange(value)}
                                isClearable
                            />
                            {Auth.roleCanEditShiftBoard() && (
                                <>
                                    <button
                                        className="btn btn-short btn-outline-secondary px-2 ml-2"
                                        title={translate("Shifts.save_template", "Save template")}
                                        disabled={
                                            templatesMatch(this.state.new_template, this.state.selected_template) ||
                                            this.state.new_template === null ||
                                            this.state.new_template.lines.length === 0
                                        }
                                        onClick={this.handleTemplateSave}
                                    >
                                        <i className="fas fa-save align-middle" />
                                    </button>
                                    <button
                                        className="btn btn-short btn-outline-secondary px-2 ml-2"
                                        title={translate("Shifts.delete_template", "Delete template")}
                                        disabled={this.state.selected_template === null}
                                        onClick={this.handleTemplateDelete}
                                    >
                                        <i className="fas fa-trash align-middle" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary ml-2"
                                        disabled={
                                            this.state.selected_template === null || this.state.isSavingShiftBoard
                                        }
                                        onClick={this.handleApplyTemplate}
                                    >
                                        {translate("Shifts.apply_template", "Apply template")}
                                    </button>
                                </>
                            )}
                            <TextInputModal
                                show={this.state.show_template_name_modal}
                                title={translate("Shifts.enter_template_name", "Enter template name")}
                                label={translate("Shifts.template_name", "Template name")}
                                value={this.state.new_template ? this.state.new_template.name : ""}
                                error={translate(
                                    "Shifts.template_already_exists",
                                    "Template already exists. Are you sure you want to override it?"
                                )}
                                isValid={this.isTemplateNameValid}
                                onAccept={this.handleTemplateNameModalAccept}
                                onCancel={this.handleTemplateNameModalCancel}
                            />
                            <ConfirmationModal
                                title={translate("Shifts.delete_template", "Delete template")}
                                accept_label={translate("common.delete", "Delete")}
                                cancel_label={translate("common.cancel", "Cancel")}
                                dangerous={true}
                                show={this.state.show_delete_template_modal}
                                onAccept={this.handleTemplateDeleteAccept}
                                onCancel={this.handleTemplateDeleteCancel}
                            >
                                {translate(
                                    "Shifts.are_you_sure_delete_template",
                                    "Are you sure you want to delete this template?"
                                )}{" "}
                                <strong>
                                    {this.state.selected_template !== null ? this.state.selected_template.name : ""}
                                </strong>
                            </ConfirmationModal>
                        </div>
                    )}
                </li>
            </React.Fragment>
        );
    }

    /////////////////////////////////////////
    // People View

    renderFilter(view: PeopleViewParams) {
        if (view) {
            if (view.type === VIEW_TYPES.shifts.id) {
                return this.renderShiftFilter();
            }
        }
        // nothing valid selected
        return <li className="list-inline-item"></li>;
    }

    renderContent(view: PeopleViewParams) {
        if (view) {
            if (view.type === VIEW_TYPES.shifts.id) {
                return (
                    <ShiftAssignments
                        selected_plant={this.props.plant_uuid}
                        selected_line_group={this.props.line_group_uuid}
                        week={this.state.shifts_filter.week}
                        year={this.state.shifts_filter.year}
                        history={this.props.history}
                        location={this.props.location}
                        match={this.props.match}
                        ref={this.ShiftAssignmentsRef}
                        template_mode={this.state.template_mode}
                        template={this.state.new_template}
                        onTemplateChange={this.handleTemplateChange}
                        onShiftSaveStateChange={this.handleShiftSaveStateChange}
                        onWeekChange={increment => {
                            this.handleShiftWeekClick(increment);
                        }}
                    />
                );
            } else if (view.type === VIEW_TYPES.people_insights.id) {
                const insight_types = [INSIGHT_TYPES.no_person, INSIGHT_TYPES.no_shift];
                return (
                    <InsightsView
                        history={this.props.history}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        insight_types={insight_types}
                        from_diff={-8 * TIME_RANGES.HOUR}
                    />
                );
            } else if (view.type === VIEW_TYPES.workforce_kpis.id) {
                return <WorkforceKPIs plant_uuid={this.props.plant_uuid} />;
            } else {
                return <div></div>;
            }
        }
        // nothing valid selected
        return <div></div>;
    }

    renderViewSelect() {
        return (
            <select
                name="select_view"
                className="form-control select_top_control"
                value={this.state.selected_view}
                onChange={this.handleViewChange}
            >
                {this.state.views.map((view, i) =>
                    view.title_code ? (
                        <option key={i} value={view.id}>
                            {translate(view.title_code, view.title)}
                        </option>
                    ) : (
                        <option key={i} value={view.id}>
                            {view.title}
                        </option>
                    )
                )}
            </select>
        );
    }

    renderEmpty() {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);

        return line_group ? (
            <strong>
                <FormattedMessage
                    id="Manufacturing.PeopleView.no_views"
                    defaultMessage="Managing people currently not supported for"
                />{" "}
                "{line_group.title}"
            </strong>
        ) : null;
    }

    shouldHideFilter = (view?: PeopleViewParams): boolean => {
        if (!view) {
            return true;
        }

        const view_types = [VIEW_TYPES.shifts.id];

        return !view_types.includes(view.type);
    };

    renderFilterContainer = (view?: PeopleViewParams) => {
        const should_hide_filter = this.shouldHideFilter(view);
        if (should_hide_filter) {
            return null;
        }

        return (
            <div className="views-filter-bar views_always_top" key="B">
                <div className="row">
                    <div className="col-lg-12 col-md-12">
                        <ul className="list-inline">
                            {view && view.id !== VIEW_TYPES.shifts.id && (
                                <li className="list-inline-item">{!view && this.renderEmpty()}</li>
                            )}
                            {view && this.renderFilter(view)}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        const should_hide_filter = this.shouldHideFilter(view);
        const style = {
            paddingTop: view && view.type === VIEW_TYPES.shifts.id ? "90px" : "",
        };
        return (
            <div className="people-view">
                <NavigationBarWithDropdown
                    handleViewChange={this.handleViewChange}
                    key="A"
                    nav="views"
                    nav_id="people"
                    location={this.props.location}
                    line_group_uuid={this.props.line_group_uuid}
                    plant_uuid={this.props.plant_uuid}
                />
                {this.renderFilterContainer(view)}
                <div
                    style={style}
                    className={classNames(`views-content people-view view-${this.state.selected_view}`, {
                        "hide-filter": should_hide_filter,
                    })}
                    key="C"
                >
                    {view && this.renderContent(view)}
                </div>
            </div>
        );
    }
}

export default PeopleView;
